import { Component, ViewChild, Output, Input, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { DxDataGridModule } from 'devextreme-angular';
import { DxoEditingModule, DxiColumnModule } from 'devextreme-angular/ui/nested';
import { PasswordComponent, PasswordComponentValue } from '../controls/components/basic/password/password.component';
import { LoadingPanelService } from '../shared/loading-panel/loading-panel.service';
import { GenericModalService, ModalSettingsMessage } from '../shared/generic-modal/generic-modal.service';
import { FileHttpService } from '../shared/http/file-http.service';
import { SvgIconComponent } from '../shared/icons/svg-icon.component';
import { ToastService, ToastType } from '../shared/toast/toast.service';
import { PartnerFtpService } from './services/partner-ftp.service';
import { IntegratedFtpConfiguration } from './models/integrated-ftp-configuration';
import { IntegratedFtpCreateContainer } from './models/integrated-ftp-create-container';
import { FTPAuthenticationType } from './models/ftp-authentication-type';
import { Partner } from './models/partner';

@Component({
    templateUrl: './new-partner-ftp.component.html',
    selector: 'ep-new-partner-ftp',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, NgClass, SvgIconComponent, DxDataGridModule, DxoEditingModule, DxiColumnModule, PasswordComponent]
})
export class NewPartnerFtpComponent {

    constructor(private partnerFtpService: PartnerFtpService, private loadingPanelService: LoadingPanelService, private modalService: GenericModalService, private toastService: ToastService, private fileHttpService: FileHttpService) { }

    public username: string;
    public password: PasswordComponentValue;
    public pluginName: string;
    public file: File[];
    @ViewChild('fileInput') fileInputVar;
    public ftpFolders: FolderWrapper[] = [];
    public partner: Partner;
    public excludeFromIntegratedFtpRetrieve: boolean = false;
    public showUpload: boolean = false;
    public showPassword: boolean = true;
    public authenticationType: string ;
    public supportPublicKey: boolean = false;
    @Input() canChangeUsername: boolean = false;

    @Output() saved: EventEmitter<IntegratedFtpConfiguration> = new EventEmitter<IntegratedFtpConfiguration>();
    @Output() cancelled: EventEmitter<void> = new EventEmitter<void>();

    public initialize(partner: Partner, supportPublicKey: boolean, authenticationType: string, showUpload: boolean, showPassword: boolean ) {
        this.password = null;
        this.supportPublicKey = supportPublicKey;
        this.partner = partner;
        this.authenticationType = authenticationType;
        this.showUpload = showUpload;
        this.showPassword = showPassword;

        this.generateUsername();
        this.partnerFtpService.getPluginName().then((pluginName) => { this.pluginName = pluginName; });
        this.loadingPanelService.showLoadingPanelDirect('Loading default folders...');
        this.partnerFtpService.getInitialSubDirectories().then(directories => {
            this.ftpFolders = directories.map(d => { return new FolderWrapper(d) });
            this.loadingPanelService.hideLoadingPanel();
        });
    }

    cancel() {
        //reset the values
        if (this.showUpload) {
            this.file = null;
            this.fileInputVar.nativeElement.value = "";
        }
        this.excludeFromIntegratedFtpRetrieve = false;
        this.cancelled.emit();
    }

    validate() {

        let missingFields: string[] = [];
        let isMissingFields: boolean = false;
        if (this.supportPublicKey) {
            if ((this.authenticationType != FTPAuthenticationType.Password ) && (!(this.file))) {
                missingFields.push('Please choose a public key.');
                isMissingFields = true;
            }

            if ((this.authenticationType != FTPAuthenticationType.PublicKey && (!this.password?.value))) {
                missingFields.push('please enter the password.');
                isMissingFields = true;
            }
        }
        else { //validate password only
            if (!this.password?.value) {
                missingFields.push('Please enter the password.');
                isMissingFields = true;            }
        }

        if (isMissingFields) {
            let settings = new ModalSettingsMessage('Required fields', 'The following fields are required:<br>' + missingFields.join('<br>'));
            this.modalService.message(settings).then((e) => { }).catch((e) => { });
            return false;
        }

        return true;
    }

    onPublickKeyChange(event) {
        if (event.target.files && event.target.files.length > 0) {
            this.file = event.target.files;
        }
    }

    generateUsername() {
        this.partnerFtpService.generateUsername().then((generatedUsername) => { this.username = generatedUsername; });
    }

    save() {

        if (!this.validate()) {
            return;
        }

        this.loadingPanelService.showLoadingPanelDirect('Saving Ftp Account...');
        let folders = this.ftpFolders.map(f => f.folder);
        let createContainer = new IntegratedFtpCreateContainer();

        createContainer.PartnerId = this.partner.PartnersId;
        createContainer.Password = this.password?.value ?? '';
        createContainer.SubDirectories = folders;
        createContainer.Username = this.username;
        createContainer.ExcludeFromIntegratedFtpRetrieve = this.excludeFromIntegratedFtpRetrieve;

        if (this.showUpload && this.file) {
            this.fileHttpService.upload<string>('/api/partners/ftp/upload-public-key', this.file).then((result: string) => {
                createContainer.FileIdentifier = result;
                this.createFtp(createContainer);
            }).catch(() => {
                this.loadingPanelService.hideLoadingPanel();
                this.toastService.showToast('Unable to upload public key...', ToastType.error);
            });
        }
        else {

            this.createFtp(createContainer);
        }

    }

    createFtp(createContainer) {
        this.partnerFtpService.createFtp(createContainer).then(result => {
            this.loadingPanelService.hideLoadingPanel();
            if (result.PartnerAlreadyHasAccount) {
                this.modalService.messageDirect('Partner already has account', 'Cannot create a new account for the partner since one already exists.');
                this.saved.emit(result.CreatedIntegratedFtpConfiguration);
            }
            else if (result.UsernameAlreadyExists) {
                this.modalService.messageDirect('Username in use', 'The given username is already in use.');
            }
            else {
                // reset if we have upload control
                if (this.showUpload) {
                    this.fileInputVar.nativeElement.value = "";
                    this.file = null;
                }

                this.toastService.showToast('FTP account created...', ToastType.success);
                this.saved.emit(result.CreatedIntegratedFtpConfiguration);
            }
        }).catch(() => {
            this.loadingPanelService.hideLoadingPanel();
            this.toastService.showToast('An error occurred while creating the FTP account...', ToastType.error);
        });
    }
}

class FolderWrapper {
    constructor(public folder: string) { }
}
